import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  Input,
  Output,
} from '@angular/core'
import { UntilDestroy } from '@ngneat/until-destroy'
import { MatIconModule } from '@angular/material/icon'
import { L10nService } from '@wm-front/admin/l10n'
// eslint-disable-next-line @nx/enforce-module-boundaries
import { RoutedFiltersRouteManagerService } from '@wm-front/shared/routed-filters-service'
import { SimpleFilterComponent } from '../simple-filter/simple-filter.component'
import {
  SimpleFilterChangeEvent,
  SimpleFilterConfiguration,
  SimpleFilterDefaultDataType,
} from '../../data/interfaces'
import { IconButtonComponent } from '@wm-front/shared/buttons'
import { FiltersCatalogService } from '../../data/filters-catalog.service'
import { toObservable, toSignal } from '@angular/core/rxjs-interop'
import { map, Subject, switchMap } from 'rxjs'
import { FetchUserGQL } from '../../data/graphql/fetch-users.types'
import { UserRole } from '@wm-front/shared/enums'

@UntilDestroy()
@Component({
  selector: 'wm-front-simple-filters-list-menu',
  templateUrl: './simple-filters-menu.component.html',
  styleUrls: ['./simple-filters-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, SimpleFilterComponent, IconButtonComponent],
})
export class SimpleFiltersMenuComponent {
  /** The filter list in menu is open or close */
  @Input() public isOpen = false

  @Output() public closePanel: EventEmitter<boolean> = new EventEmitter()

  public menuIsExtended = input<boolean>(false)

  public filterConfigurationService = inject(FiltersCatalogService)

  public l10nService = inject(L10nService)

  public reset = new Subject<void>()

  /** Only display the branch manager filter when strictly more than one manager exists. */
  public displayBranchManagerFilter = computed(
    () => this._branchManagers().length > 1
  )

  public configurations = computed<SimpleFilterConfiguration<any>[]>(() => [
    ...(this.displayBranchManagerFilter()
      ? [
          this.filterConfigurationService.getConfiguration({
            display: {
              label: $localize`:@@FiltersMenuBranchManagerButton:Branch manager`,
              placeholder: $localize`:@@FiltersBranchManagerPlaceholderLabel:Trouvez un branch manager`,
              emptyResult: $localize`:@@FiltersBranchManagerNotFoundOptionLabel:Aucun branch manager trouvé`,
            },
            dataType: SimpleFilterDefaultDataType.BRANCH_MANAGERS,
            key: 'persistentManagerUserIds',
            urlName: 'persistentManagerUserIds',
          }),
        ]
      : []),
    this.filterConfigurationService.getConfiguration({
      display: {
        label: $localize`:@@FiltersMenuReferentUserButton:Operation manager`,
        placeholder: $localize`:@@FiltersReferentUserPlaceholderLabel:Trouvez un opération manager`,
        emptyResult: $localize`:@@FiltersReferentUserNotFoundOptionLabel:Aucun opération manager trouvée`,
      },
      dataType: SimpleFilterDefaultDataType.REFERENT_USERS,
      key: 'persistentReferentUserIds',
      urlName: 'persistentReferentUserIds',
    }),
    this.filterConfigurationService.getConfiguration({
      display: {
        label: $localize`:@@FiltersAddressButton:Adresses`,
        placeholder: $localize`:@@FiltersAutocompleteDropdownAddressPlaceholderLabel:Trouvez une adresse`,
        emptyResult: $localize`:@@FiltersAutocompleteDropdownAddressNotFoundOptionLabel:Aucune adresse trouvée`,
      },
      dataType: SimpleFilterDefaultDataType.BUILDINGS,
      key: 'persistentBuildingIds',
      urlName: 'persistentBuildingIds',
      bodyConverter: (filters, state, externalState, _, reset, pagination) => {
        filters = { ...filters }
        if (externalState?.['accountIds']?.length) {
          filters.accountIds = externalState['accountIds']?.map((e) =>
            parseInt(e)
          )
        }
        filters.territories = [this.l10nService.territory$()]
        return {
          filters,
          pagination,
        }
      },
    }),
    this.filterConfigurationService.getConfiguration({
      dataType: SimpleFilterDefaultDataType.ACCOUNT,
      key: 'persistentAccountIds',
      urlName: 'persistentAccountIds',
    }),
    this.filterConfigurationService.getConfiguration({
      dataType: SimpleFilterDefaultDataType.SUPPLY_MANAGER,
      key: 'persistentSupplyManager',
      urlName: 'persistentSupplyManager',
    }),
  ])

  /** Active Filter */
  public activeFilter: string

  public filtersFacade = inject(RoutedFiltersRouteManagerService)

  public values = toSignal(this.filtersFacade.filters$)

  protected _cd = inject(ChangeDetectorRef)

  private _branchManagerQuery = inject(FetchUserGQL)

  /** Branch managers available for the current territory. Used to decide whether to display the filter. */
  private _branchManagers = toSignal(
    toObservable(this.l10nService.territory$).pipe(
      switchMap((territory) =>
        this._branchManagerQuery
          .fetch({
            filters: {
              roles: [UserRole.Admin],
              isBranchManager: true,
              countryCodes: [territory as string],
            },
            pagination: { first: 1000 },
          })
          .pipe(map((res) => res.data.usersv2.edges.map(({ node }) => node)))
      )
    ),
    { initialValue: [] }
  )

  /** Close filter panel */
  public closePanelAction(): void {
    this.closeAutocompletePanel()
    this.closePanel.emit(false)
  }

  /** Listener for the close event of the autocomplete components */
  public closeAutocompletePanel(): void {
    this.activeFilter = ''
  }

  public async handleOptionPicked(
    event: SimpleFilterChangeEvent
  ): Promise<void> {
    await this.filtersFacade.updateCurrentRouteWithFilters(
      { [event.key]: event.value as string },
      this.configurations()
    )
  }

  /**
   * Reset all selected options filters
   */
  public async resetAllSelectedOptions(): Promise<void> {
    this.reset.next()
    await this.filtersFacade.updateCurrentRouteWithFilters({
      persistentBuildingIds: '',
      persistentReferentUserIds: '',
      persistentManagerUserIds: '',
      persistentAccountIds: '',
      persistentSupplyManager: '',
    })
  }
}
